import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Send, Trash2, Pin, MessageCircle, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatPanel = ({ studioPin = null, className = "" }) => {
  const [messages, setMessages] = useState([]);
  const [username, setUsername] = useState(() => {
    try { return localStorage.getItem("sdlw_username") || ""; } catch(_) { return ""; }
  });
  const [text, setText] = useState("");
  const [pinNext, setPinNext] = useState(false);
  const listRef = useRef(null);
  const isStudio = !!studioPin;

  const load = async () => {
    try {
      const r = await axios.get(`${API}/chat`);
      setMessages(r.data);
    } catch(_) {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    try {
      if (isStudio) {
        await axios.post(`${API}/chat/studio`, { text: text.trim(), pinned: pinNext }, { headers: { "X-Studio-Pin": studioPin } });
      } else {
        const name = (username || "Viewer").trim() || "Viewer";
        try { localStorage.setItem("sdlw_username", name); } catch(_) {}
        await axios.post(`${API}/chat`, { username: name, text: text.trim() });
      }
      setText(""); setPinNext(false);
      load();
    } catch(_) {}
  };

  const del = async (id) => {
    if (!isStudio) return;
    try { await axios.delete(`${API}/chat/${id}`, { headers: { "X-Studio-Pin": studioPin } }); load(); } catch(_){}
  };

  const clearAll = async () => {
    if (!isStudio) return;
    if (!window.confirm("Clear all chat messages?")) return;
    try { await axios.post(`${API}/chat/clear`, {}, { headers: { "X-Studio-Pin": studioPin } }); load(); } catch(_){}
  };

  return (
    <div className={`glass rounded-xl flex flex-col ${className}`}>
      <div className="px-4 py-2.5 border-b border-white/5 flex items-center gap-2">
        <MessageCircle className="w-4 h-4 text-cyan-400" />
        <div className="font-semibold text-sm">Live Chat</div>
        <span className="ml-auto text-[11px] mono text-slate-500">{messages.length}</span>
        {isStudio && (
          <button onClick={clearAll} title="Clear all" className="text-slate-400 hover:text-red-400">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div ref={listRef} data-testid="chat-message-list" className="flex-1 overflow-y-auto p-3 space-y-2 text-sm min-h-0">
        {messages.length === 0 && <div data-testid="chat-empty-state" className="text-slate-500 text-xs mono">No messages yet.</div>}
        {messages.map((m) => (
          <div key={m.id} data-testid={`chat-message-${m.id}`} className={`group flex items-start gap-2 ${m.pinned ? "px-2 py-1.5 rounded bg-amber-500/10 border border-amber-500/30" : ""}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                {m.from_studio ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-300"><ShieldCheck className="w-3 h-3" /> {m.username}</span>
                ) : (
                  <span className="text-cyan-300 font-semibold mono text-[12px] truncate max-w-[10rem]">{m.username}</span>
                )}
                {m.pinned && <Pin className="w-3 h-3 text-amber-400" />}
              </div>
              <div className="text-slate-100 break-words leading-snug">{m.text}</div>
            </div>
            {isStudio && (
              <button onClick={() => del(m.id)} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
      <form onSubmit={send} data-testid="chat-form" className="p-2.5 border-t border-white/5 space-y-2">
        {!isStudio && (
          <Input data-testid="chat-username-input" value={username} onChange={(e)=>setUsername(e.target.value)} placeholder="Your name" className="bg-black/40 border-white/10 text-slate-100 h-9 text-sm" />
        )}
        <div className="flex gap-2">
          <Input data-testid="chat-message-input" value={text} onChange={(e)=>setText(e.target.value)} placeholder={isStudio ? "Post as Storm Desk…" : "Say something…"} className="bg-black/40 border-white/10 text-slate-100 h-9 text-sm" />
          <Button data-testid="chat-send-button" type="submit" className="bg-red-600 hover:bg-red-500 text-white h-9 px-3"><Send className="w-4 h-4" /></Button>
        </div>
        {isStudio && (
          <label className="flex items-center gap-2 text-[11px] mono text-slate-400">
            <input type="checkbox" checked={pinNext} onChange={(e)=>setPinNext(e.target.checked)} className="accent-red-500" />
            Pin this message
          </label>
        )}
      </form>
    </div>
  );
};

export default ChatPanel;
